import { assertNever } from '@dadohr/shared/domainTypes/00-assertNever';
import { getItemDisplayName, resolveExpTitle } from '../../helpers/adminName';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchorType,
  ExperienceEventType,
  ExperienceStatus,
  IExperience,
  IExperienceEvent,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY,
  SubTaskStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Tooltip } from '@material-ui/core';
import { flatten, uniq } from 'lodash';
import { AlertTriangle } from 'lucide-react';
import { Moment } from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import {
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  RowsRendererScrollContainer,
  useSortQueryParam
} from '../../../../components/GroupableList';
import { Loader } from '../../../../components/Loader';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import {
  getAllActorUserIds,
  isExperienceUserActor
} from '../../../../services/experiences/instance';
import { getTaskTemplateForTaskId } from '../../../../services/experiences/template';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { toMoment } from '../../../../services/time';
import {
  matchUserBySearchTerm,
  toFullName,
  toSearchTermRegexp,
  useUserMapById
} from '../../../../services/user';
import { getAnchorLabel } from '../../pages/TemplateEdit/EnrollmentPreview';
import { useAnchorFields } from '../Anchors';
import { InstanceStatusBar } from '../InstanceStatusBar';
import { InstanceStatusChip } from '../InstanceStatusChip';
type ColumnName =
  | 'name'
  | 'launchDate'
  | 'supportingActorName'
  | 'department'
  | 'location'
  | 'role'
  | 'anchoredDate'
  | 'lastEvent'
  | 'participantStatus'
  | 'actions';

type D = {
  exp: Doc<IExperience>;
  actors: { [actorKey: string]: Doc<IUser> | null };
};

type OtherProps = {
  tpl: Doc<IExperienceTemplate>;
  actor: { key: string; label: string };
  anchorLabel: string;
};

type Column = IColumn<D, ColumnName, OtherProps>;

const USE_MANUAL_REMINDERS = false;

const LastEventDate = styled('span')((p) => ({
  color: p.theme.custom.colors.gray.w500
}));

// eslint-disable-next-line
const Actions = ({ d }: { d: D }) => {
  if (d.exp.data.status !== ExperienceStatus.LATE) {
    return null;
  }
  if (USE_MANUAL_REMINDERS) {
    return <Button size="small">Remind</Button>;
  }
  return null;
};

const EventStatusLineWithDate: React.FC<{ event: IExperienceEvent }> = ({
  event,
  children
}) => {
  return (
    <>
      <LastEventDate>{toMoment(event.at).format('YYYY/MM/DD')}</LastEventDate>{' '}
      {children}
    </>
  );
};

const EventStatusLine = ({
  event,
  tpl,
  exp
}: {
  event: IExperienceEvent | null;
  tpl: Doc<IExperienceTemplate>;
  exp: Doc<IExperience>;
}) => {
  const getTaskName = useCallback(
    (taskId: string | null) => {
      const taskTpl = taskId
        ? getTaskTemplateForTaskId(tpl, exp, taskId)
        : null;
      return taskTpl ? resolveExpTitle(getItemDisplayName(taskTpl), tpl.data.title) : '';
    },
    [exp, tpl]
  );
  // TODO translate
  if (!event) {
    return null;
  }
  if (event.type === ExperienceEventType.EXP_ENROLL) {
    return (
      <EventStatusLineWithDate event={event}>
        Enrolled in experience
      </EventStatusLineWithDate>
    );
  }
  if (event.type === ExperienceEventType.EXP_START) {
    return (
      <EventStatusLineWithDate event={event}>
        Started experience
      </EventStatusLineWithDate>
    );
  }

  if (event.type === ExperienceEventType.TASK_START) {
    return (
      <EventStatusLineWithDate event={event}>
        Started task {getTaskName(event.taskId)}
      </EventStatusLineWithDate>
    );
  }
  if (event.type === ExperienceEventType.TASK_DONE) {
    return (
      <EventStatusLineWithDate event={event}>
        Completed task {getTaskName(event.taskId)}
      </EventStatusLineWithDate>
    );
  }
  if (
    event.type === ExperienceEventType.TASK_REMINDER ||
    event.type === ExperienceEventType.TASK_LATE_REMINDER
    // event.type === ExperienceEventType.TASK_LATE_REMINDER_MANUAL
  ) {
    return (
      <EventStatusLineWithDate event={event}>
        Received reminder for {getTaskName(event.taskId)}
      </EventStatusLineWithDate>
    );
  }
  if (event.type === ExperienceEventType.TASK_UNDONE) {
    return (
      <EventStatusLineWithDate event={event}>
        Uncompleted task {getTaskName(event.taskId)}
      </EventStatusLineWithDate>
    );
  }
  if (event.type === ExperienceEventType.TASK_LATE) {
    return (
      <EventStatusLineWithDate event={event}>
        Missed deadline for {getTaskName(event.taskId)}
      </EventStatusLineWithDate>
    );
  }

  if (event.type === ExperienceEventType.SUB_TASK_DONE) {
    return null;
  }
  if (event.type === ExperienceEventType.SUB_TASK_SKIPPED) {
    return null;
  }
  if (event.type === ExperienceEventType.SUB_TASK_UNSKIPPED) {
    return null;
  }
  if (event.type === ExperienceEventType.SUB_TASK_UNDONE) {
    return null;
  }

  if (event.type === ExperienceEventType.EXP_DONE) {
    return (
      <EventStatusLineWithDate event={event}>
        Completed experience
      </EventStatusLineWithDate>
    );
  }
  if (event.type === ExperienceEventType.EXP_MESSAGE) {
    return null;
  }
  console.log('Unhandled event type detected', event);
  return null;
};

const withActorOrNull = <T extends unknown>(
  d: D,
  actor: string,
  fn: (user: Doc<IUser>) => T
): T | null => {
  const a = d.actors[actor];
  return a ? fn(a) : null;
};

const withParticipantOrNull = <T extends unknown>(
  d: D,
  fn: (user: Doc<IUser>) => T
): T | null => withActorOrNull(d, PARTICIPANT_ACTOR_KEY, fn);

type CellFn = (rowData: D, rowProps: OtherProps) => React.ReactNode;
const CELLS: {
  lastEvent: CellFn;
  status: CellFn;
} = {
  lastEvent: (d, { tpl, actor }) => (
    <EventStatusLine
      event={d.exp.data.latestEvents?.[actor.key] || null}
      tpl={tpl}
      exp={d.exp}
    />
  ),
  status: (d, o) =>
    d.exp.data.status === ExperienceStatus.SUGGESTED ? (
      <InstanceStatusChip status={d.exp.data.status} />
    ) : (
      <InstanceStatusBar
        d={d.exp}
        height="24px"
        tpl={o.tpl}
        actor={o.actor.key}
      />
    )
};

export const AnchorDateDiffWarning = ({
  original,
  current
}: {
  original: Moment | null;
  current: Moment | null;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  if (!original) {
    return null;
  }
  if (original.isSame(current, 'day')) {
    return null;
  }
  const warning = t(
    `This date applies to this experience only. It differs from the user's profile ({{ date }}).`,
    {
      date: original?.format('YYYY/MM/DD')
    }
  );
  return (
    <Tooltip title={warning}>
      <AlertTriangle size={16} color={theme.custom.colors.yellow.w700} />
    </Tooltip>
  );
};

const PARTICIPANT_COLUMNS: Column[] = [
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) =>
      withParticipantOrNull(
        d,
        (u) => toFullName(u.data) || u.data.email || u.data.personalEmail
      ),
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'launchDate',
    head: () => 'Exp. launch',
    cell: (d) => d.exp.data.launchAt ? toMoment(d.exp.data.launchAt).format('YYYY/MM/DD') : '-',
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'anchoredDate',
    head: (o) => {
      if (o?.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
        return o.anchorLabel;
      }
    },
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        if (
          o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
          o.tpl.data.anchor.field === 'hireDate'
        ) {
          const originalDate =
            u.data.hireDate && toMoment(u.data.hireDate).tz(o.tpl.data.tz);
          // This is a rare expception for broken instances which could NOT be migrated when this field was introduced
          if (!d.exp.data.anchorDate?.at) {
            return (
              <FlexContainer>
                <div>{originalDate?.format('YYYY/MM/DD')}</div>
              </FlexContainer>
            );
          }
          const currentDate = toMoment(d.exp.data.anchorDate.at).tz(
            o.tpl.data.tz
          );
          return (
            <FlexContainer>
              <div>{currentDate.format('YYYY/MM/DD')}</div>
              <AnchorDateDiffWarning
                original={originalDate}
                current={currentDate}
              />
            </FlexContainer>
          );
        }
        if (o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
          return (
            toMoment(d.exp.data.anchorDate.at)
              .tz(o.tpl.data.tz)
              .format('YYYY/MM/DD') || ''
          );
        }
      }),
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'location',
    head: () => 'Location',
    cell: (d) => withParticipantOrNull(d, (u) => u.data.location),
    align: 'left',
    sortable: true,
    width: 100,
    flexGrow: 1
  },
  {
    key: 'department',
    head: () => 'Department',
    cell: (d) => withParticipantOrNull(d, (u) => u.data.department),
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'role',
    head: () => 'Role',
    cell: (d) => withParticipantOrNull(d, (u) => u.data.role),
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 2
  },
  {
    key: 'lastEvent',
    head: () => 'Last event',
    cell: CELLS.lastEvent,
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 5
  },
  {
    key: 'participantStatus',
    head: () => 'Status',
    cell: CELLS.status,
    align: 'left',
    sortable: true,
    width: 112,
    flexGrow: 0,
    showOverflowOnHover: false
  }
  // {
  //   key: 'actions',
  //   head: () => '',
  //   alternateHead: () => 'Actions',
  //   cell: (d) => <Actions d={d} />,
  //   sortable: false,
  //   align: 'left',
  //   width: 100,
  //   flexGrow: 0
  // }
];

const SUPPORTING_ACTOR_COLUMNS: Column[] = [
  {
    key: 'supportingActorName',
    head: (o) => o?.actor.label,
    cell: (d, o) => {
      console.log(o.actor);
      return withActorOrNull(d, o.actor.key, (u) => toFullName(u.data));
    },
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'name',
    head: () => 'Participant',
    cell: (d) => withParticipantOrNull(d, (u) => toFullName(u.data)),
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'launchDate',
    head: () => 'Exp Launch Date',
    cell: (d) => d.exp.data.launchAt ? toMoment(d.exp.data.launchAt).format('YYYY/MM/DD') : '-',
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'anchoredDate',
    head: (o) => {
      if (o?.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
        return o.anchorLabel;
      }
    },
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        if (
          o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
          o.tpl.data.anchor.field === 'hireDate'
        ) {
          const originalDate =
            u.data.hireDate && toMoment(u.data.hireDate).tz(o.tpl.data.tz);
          // This is a rare expception for broken instances which could NOT be migrated when this field was introduced
          if (!d.exp.data.anchorDate?.at) {
            return (
              <FlexContainer>
                <div>{originalDate?.format('YYYY/MM/DD')}</div>
              </FlexContainer>
            );
          }
          const currentDate = toMoment(d.exp.data.anchorDate.at).tz(
            o.tpl.data.tz
          );
          return (
            <FlexContainer>
              <div>{currentDate.format('YYYY/MM/DD')}</div>
              <AnchorDateDiffWarning
                original={originalDate}
                current={currentDate}
              />
            </FlexContainer>
          );
        }
        if (o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
          return (
            toMoment(d.exp.data.anchorDate.at)
              .tz(o.tpl.data.tz)
              .format('YYYY/MM/DD') || ''
          );
        }
      }),
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'lastEvent',
    head: () => 'Last Event',
    cell: CELLS.lastEvent,
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 5
  },
  {
    key: 'participantStatus',
    head: () => 'Status',
    cell: CELLS.status,
    align: 'left',
    sortable: true,
    width: 112,
    flexGrow: 0
  }
  // {
  //   key: 'actions',
  //   head: () => '',
  //   alternateHead: () => 'Actions',
  //   cell: (d) => <Actions d={d} />,
  //   sortable: false,
  //   align: 'left',
  //   width: 100,
  //   flexGrow: 0
  // }
];

const getTaskLevelSortValue = (d: Doc<IExperience>) => {
  let m = 0;
  d.data.tasks.forEach((t) => {
    switch (t.status) {
      case TaskStatus.LATE:
        break;
      case TaskStatus.NOT_STARTED:
        m += 1;
        break;
      case TaskStatus.RUNNING:
        m += 2;
        break;
      case TaskStatus.COMPLETE:
        m += 3;
        break;
      case TaskStatus.SKIPPED:
        m += 4;
        break;
      case TaskStatus.PAST_DEADLINE:
        // TODO check this
        // Not sure what this is for so I won't do anything and just fix the type error
        break;
      default:
        assertNever(t.status);
    }
  });
  return m;
};

const getSubTaskLevelSortValue = (d: Doc<IExperience>) => {
  let m = 0;
  d.data.tasks.forEach((t) => {
    t.subTasks.forEach((s) => {
      switch (s.status) {
        case SubTaskStatus.OPEN:
          break;
        case SubTaskStatus.SKIPPED:
          m += 1;
          break;
        case SubTaskStatus.DONE:
          m += 2;
          break;
        default:
          assertNever(s.status);
      }
    });
  });
  return m;
};

const SORTERS: ItemSorters<D, OtherProps> = {
  anchoredDate: {
    key: 'anchoredDate',
    items: {
      sort: (d) => d.exp.data.anchorDate.at.toMillis(),
      dir: 'desc'
    }
  },
  name: {
    key: 'name',
    items: {
      sort: (d) => withParticipantOrNull(d, (u) => u.data.lastName) || '',
      dir: 'asc'
    }
  },
  supportingActorName: {
    key: 'supportingActorName',
    items: {
      sort: (d, o) =>
        withActorOrNull(d, o.actor.key, (u) => u.data.lastName) || '',
      dir: 'asc'
    }
  },
  launchDate: {
    key: 'launchDate',
    items: {
      sort: (d) => d.exp.data.launchAt?.toMillis() || Infinity,
      dir: 'desc'
    }
  },
  department: {
    key: 'department',
    items: {
      sort: (d) => withParticipantOrNull(d, (u) => u.data.department) || '',
      dir: 'asc'
    }
  },
  location: {
    key: 'location',
    items: {
      sort: (d) => withParticipantOrNull(d, (u) => u.data.location) || '',
      dir: 'asc'
    }
  },
  role: {
    key: 'role',
    items: {
      sort: (d) => withParticipantOrNull(d, (u) => u.data.role) || '',
      dir: 'asc'
    }
  },
  hireDate: {
    key: 'hireDate',
    items: {
      sort: (d) =>
        withParticipantOrNull(d, (u) => u.data.hireDate?.toMillis()) || 0,
      dir: 'asc'
    }
  },
  lastEvent: {
    key: 'lastEvent',
    items: {
      sort: (d) => d.exp.data.lastEvent?.at.toMillis() || 0,
      dir: 'desc'
    }
  },
  participantStatus: {
    key: 'participantStatus',
    items: {
      sort: (d) => {
        const taskLevel = getTaskLevelSortValue(d.exp);
        const subTaskLevel = getSubTaskLevelSortValue(d.exp);
        return taskLevel * 10 + subTaskLevel;
      },
      dir: 'asc'
    }
  }
};

const ROW_TO_KEY = (d: D) => d.exp.id;

export const ParticipantsTable = ({
  tpl,
  ds,
  height,
  onRowClick,
  rowToHrefExternal,
  q,
  actor
}: {
  tpl: Doc<IExperienceTemplate>;
  ds: Doc<IExperience>[];
  height: number;
  onRowClick?: (d: Doc<IExperience>) => void;
  rowToHrefExternal?: (d: Doc<IExperience>) => string;
  q?: string;
  actor: { key: string; label: string };
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const anchorFields = useAnchorFields(org.customUserFields);
  const anchorLabel = getAnchorLabel(tpl.data.anchor, anchorFields, t) || '';
  const userIds = useMemo(
    () => uniq(flatten(ds.map((d) => getAllActorUserIds(d)))),
    [ds]
  );
  const toRows = useCallback(
    (userMap: { [key: string]: Doc<IUser> }) =>
      ds.map<D>((exp) => {
        const actors: { [actorKey: string]: Doc<IUser> } = {};
        exp.data.actors.filter(isExperienceUserActor).forEach((a) => {
          actors[a.key] = userMap[a.userId] || null;
        });
        return {
          exp,
          actors: actors
        };
      }),
    [ds]
  );

  const [rows, loading] = useMappedLoadingValue(
    useUserMapById(tpl.data.orgId, userIds),
    toRows,
    true
  );
  const filteredRows = useMemo(() => {
    if (!rows || !q) {
      return rows;
    }
    const regexp = toSearchTermRegexp(q);
    return rows.filter((r) => {
      const matchesParticipant = withParticipantOrNull(r, (u) =>
        matchUserBySearchTerm(regexp, u)
      );
      return (
        matchesParticipant ||
        (actor.key !== PARTICIPANT_ACTOR_KEY &&
          withActorOrNull(r, actor.key, (u) =>
            matchUserBySearchTerm(regexp, u)
          ))
      );
    });
  }, [rows, q, actor]);

  const [[sorter, direction], setSort] = useSortQueryParam('sort', SORTERS);
  const _onRowClick = useCallback(
    (d: D) => (onRowClick ? onRowClick(d.exp) : undefined),
    [onRowClick]
  );
  const _rowToHrefExternal = useCallback(
    (d: D) => (rowToHrefExternal ? rowToHrefExternal(d.exp) : null),
    [rowToHrefExternal]
  );
  const otherProps: OtherProps = useMemo(() => ({ tpl, actor, anchorLabel }), [
    tpl,
    actor,
    anchorLabel
  ]);

  const filteredColumns = useMemo(() => {
    const columns =
      actor.key === PARTICIPANT_ACTOR_KEY
        ? PARTICIPANT_COLUMNS
        : SUPPORTING_ACTOR_COLUMNS;

    if (tpl.data.anchor.type !== ExperienceAnchorType.EMPLOYEE_DATE) {
      return columns.filter((col) => col.key !== 'anchoredDate');
    }

    return columns;
  }, [tpl, actor]);

  const activeSorter =
    sorter ||
    (filteredColumns.some((c) => c.key === 'anchoredDate')
      ? SORTERS.anchoredDate
      : SORTERS.launchDate);

  if (loading) {
    return <Loader height={height} />;
  }
  if (!filteredRows) {
    // error
    return null;
  }

  const tableContainerStyle = {
    margin: '8px 0'
  };

  return (
    <RowsRendererScrollContainer height={height} style={tableContainerStyle}>
      <RowsRenderer
        variant="contained"
        columns={filteredColumns}
        rows={filteredRows}
        rowToKey={ROW_TO_KEY}
        rowToHrefExternal={_rowToHrefExternal}
        onRowClick={_onRowClick}
        sorter={activeSorter}
        sortDirection={direction || activeSorter.items.dir}
        renderHead={true}
        headProps={{ sticky: true, offset: 0 }}
        onHeadClick={(c, dir) => setSort([SORTERS[c.key] || null, dir])}
        chunkSize={30}
        rootMargin="300px"
        rowHeight={ROW_HEIGHTS.dense}
        otherProps={otherProps}
      />
    </RowsRendererScrollContainer>
  );
};
